import { ArcRotateCamera, Color4, Scene, Vector3, type AbstractEngine, type SceneOptimizer } from "@babylonjs/core";
import { SkySystem } from "./environment/SkySystem";
import { DayNightCycle } from "./environment/DayNightCycle";
import { IslandTerrain } from "./environment/IslandTerrain";
import { PalmTree } from "./environment/PalmTree";
import { OceanWater } from "./environment/OceanWater";
import { PostProcessPipeline } from "./environment/PostProcessPipeline";
import { EnvironmentDecorator } from "./environment/EnvironmentDecorator";
import { JohnnyCharacter } from "./character/JohnnyCharacter";
import { MermaidVisitor } from "./character/MermaidVisitor";
import { CinematicCameraRig } from "./camera/CinematicCameraRig";
import { AudioManager } from "../audio/AudioManager";
import { DayClock } from "../director/DayClock";
import { HolidayCalendar } from "../director/HolidayCalendar";
import { LongArcState } from "../director/LongArcState";
import { SceneDirector } from "../director/SceneDirector";
import { Scheduler } from "../director/Scheduler";
import { VignettePlayer } from "../director/VignettePlayer";
import { VIGNETTE_REGISTRY } from "../director/vignette-defs";
import type { VignetteRuntimeContext } from "../director/vignette-defs/types";
import { startPerformanceGuard } from "../engine/PerformanceGuard";

const DEFAULT_CAMERA_TARGET = new Vector3(0, 1.5, 0);

/**
 * Composes the environment (Phase 1), Johnny (Phase 2), and the Scene
 * Director content system (Phase 3) together. Assembles independently
 * documented modules rather than owning rendering or content logic itself.
 *
 * The director stack (SceneDirector/Scheduler/VignettePlayer) can't be
 * built until Johnny and the mermaid have both finished their async loads —
 * `director` stays null until then, and `update()` guards accordingly.
 */
export class SceneRoot {
  readonly scene: Scene;
  readonly camera: ArcRotateCamera;
  readonly sky: SkySystem;
  readonly dayNightCycle: DayNightCycle;
  readonly dayClock: DayClock;
  readonly island: IslandTerrain;
  readonly palmTree: PalmTree;
  readonly ocean: OceanWater;
  readonly postProcess: PostProcessPipeline;
  readonly decorator: EnvironmentDecorator;
  readonly cameraRig: CinematicCameraRig;
  readonly audio: AudioManager;
  readonly holidayCalendar: HolidayCalendar;
  readonly longArcState: LongArcState;
  readonly sceneDirector: SceneDirector;
  readonly performanceGuard: SceneOptimizer;

  /** Null until the async glTF loads resolve. */
  johnny: JohnnyCharacter | null = null;
  mermaid: MermaidVisitor | null = null;
  /** Null until Johnny + the mermaid are both ready (see above). */
  private director: { scheduler: Scheduler; player: VignettePlayer } | null = null;

  constructor(engine: AbstractEngine, canvas: HTMLCanvasElement) {
    this.scene = new Scene(engine);
    this.scene.clearColor = new Color4(0, 0, 0, 1); // fully covered by the sky dome

    // Distance fog hides the ocean plane's far edge (a large-but-finite
    // mesh, not truly infinite) and reads as the reference's cool blue-grey
    // atmospheric haze on distant elements — two birds, one setting.
    // fogColor is driven live by DayNightCycle (matched to the sky's own
    // horizon color) rather than set once here — see its constructor.
    this.scene.fogMode = Scene.FOGMODE_EXP2;
    this.scene.fogDensity = 0.0035;

    this.camera = new ArcRotateCamera(
      "mainCamera",
      -Math.PI / 2.4,
      Math.PI / 2.15,
      22,
      DEFAULT_CAMERA_TARGET.clone(),
      this.scene
    );
    this.camera.lowerRadiusLimit = 6;
    this.camera.upperRadiusLimit = 40;
    this.camera.wheelDeltaPercentage = 0.01;
    this.camera.maxZ = 6000; // beyond the ocean plane's extent, so it fades in fog rather than clipping
    this.camera.attachControl(canvas, true);
    // attachControl sets canvas.tabIndex = 1 internally (so it can receive
    // keyboard focus for camera controls) — a real accessibility issue
    // (tabindex > 0 disrupts natural tab order; Lighthouse flags it).
    // tabIndex = 0 is equally focusable without that downside.
    canvas.tabIndex = 0;
    this.cameraRig = new CinematicCameraRig(this.camera, this.camera.position.clone(), DEFAULT_CAMERA_TARGET.clone());

    this.sky = new SkySystem(this.scene);
    this.dayClock = new DayClock();
    this.dayNightCycle = new DayNightCycle(this.scene, this.sky);
    this.dayNightCycle.setTimeOfDay(this.dayClock.getTimeOfDay());

    // "Small" island, per spec — a single focal palm on a modest sand spit,
    // not a sprawling landmass.
    this.island = new IslandTerrain(this.scene, 6);
    this.palmTree = new PalmTree(this.scene, new Vector3(0, this.island.groundLevel, -0.5));
    this.decorator = new EnvironmentDecorator(this.scene, this.palmTree.crownWorldPosition, this.palmTree.root.position);

    this.ocean = new OceanWater(this.scene);
    this.ocean.addToRenderList(this.sky.mesh);
    for (const mesh of this.island.root.getChildMeshes()) this.ocean.addToRenderList(mesh);
    for (const mesh of this.palmTree.root.getChildMeshes()) this.ocean.addToRenderList(mesh);

    this.postProcess = new PostProcessPipeline(this.scene, this.camera);
    this.performanceGuard = startPerformanceGuard(this.scene);

    this.audio = new AudioManager(this.scene);
    this.holidayCalendar = new HolidayCalendar();
    this.longArcState = new LongArcState();
    this.sceneDirector = new SceneDirector(VIGNETTE_REGISTRY, this.holidayCalendar);
    this.dayClock.onNewDay(() => this.longArcState.advanceDay());

    // Fire-and-forget: the environment renders immediately; Johnny and the
    // mermaid appear once their glTFs finish loading, and the director
    // stack (which needs both) comes online right after.
    const johnnySpot = new Vector3(-1.6, this.island.groundLevel, 1.4);
    // The bubble's ~0.75-unit radius means resting it at/near the sand's
    // own surface height (groundLevel) buries most of it under the opaque
    // sand mesh — only a thin sliver pokes through. Lifted clear instead.
    const mermaidSpot = new Vector3(johnnySpot.x + 2.5, this.island.groundLevel + 0.8, johnnySpot.z + 2.0);

    Promise.all([JohnnyCharacter.load(this.scene, johnnySpot), MermaidVisitor.load(this.scene, mermaidSpot)])
      .then(([johnny, mermaid]) => {
        this.johnny = johnny;
        this.mermaid = mermaid;
        for (const mesh of johnny.root.getChildMeshes()) this.ocean.addToRenderList(mesh);

        const ctx: VignetteRuntimeContext = {
          scene: this.scene,
          johnny,
          mermaid,
          cameraRig: this.cameraRig,
          audio: this.audio,
          decorator: this.decorator,
          longArcState: this.longArcState,
          islandGroundLevel: this.island.groundLevel,
        };
        const player = new VignettePlayer(ctx);
        const scheduler = new Scheduler(this.sceneDirector, player, this.dayClock, this.longArcState);
        this.director = { scheduler, player };
      })
      .catch((err) => console.error("[Castaway] failed to load Johnny/mermaid:", err));
  }

  /** Force-plays a vignette by id immediately, bypassing weighting/date gating — for debug overrides and verification. */
  forceVignette(id: string): boolean {
    if (!this.director) return false;
    const vignette = this.sceneDirector.getById(id);
    if (!vignette) return false;
    this.director.player.cancelCurrent();
    this.director.player.play(vignette);
    return true;
  }

  /** Advances everything that's time-driven: day clock/lighting, palm wind sway, camera transitions, and (once ready) the vignette scheduler. */
  update(deltaMs: number): void {
    this.dayClock.update(deltaMs);
    this.dayNightCycle.setTimeOfDay(this.dayClock.getTimeOfDay());
    this.palmTree.update(deltaMs);
    this.cameraRig.update(deltaMs);

    if (this.director) {
      this.director.scheduler.update(deltaMs);
      this.director.player.update(deltaMs);
    }
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    this.performanceGuard.dispose();
    this.postProcess.dispose();
    this.johnny?.dispose();
    this.mermaid?.dispose();
    this.decorator.dispose();
    this.audio.dispose();
    this.ocean.dispose();
    this.palmTree.dispose();
    this.island.dispose();
    this.sky.dispose();
    this.scene.dispose();
  }
}
